import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from './firebase';
import { doc, getDoc } from 'firebase/firestore';
import DOMPurify from 'dompurify';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiBook, FiClock, FiLayers, FiShare2, FiBookmark } from 'react-icons/fi';
import { useTheme } from '../context/ThemeContext';
import Footer from './Footer';
import RoadmapView from './RoadmapView';
import { BannerAd } from './AdSense';
import SEO from './SEO';

const TutorialDetail = () => {
  const { tutorialId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [tutorial, setTutorial] = useState(null);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scrollProgress, setScrollProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalScroll = document.documentElement.scrollHeight - window.innerHeight;
      const currentScroll = window.pageYOffset;
      setScrollProgress((currentScroll / totalScroll) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchTutorial = async () => {
      setLoading(true);
      try {
        const docRef = doc(db, 'posts', tutorialId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setTutorial({ id: docSnap.id, ...docSnap.data() });
          setError(null);
        } else {
          setError('The requested tutorial could not be found.');
        }
      } catch (err) {
        console.error('Error fetching tutorial:', err);
        setError('Failed to load tutorial. Please check your connection.');
      } finally {
        setLoading(false);
      }
    };

    if (tutorialId) fetchTutorial();
  }, [tutorialId]);

  const handleTopicChange = (index) => {
    setCurrentTopicIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) return <TutorialSkeleton isDarkMode={isDarkMode} />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6" style={{ background: 'var(--bg-main)' }}>
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center text-red-500 mx-auto mb-6">
            <FiBook size={40} />
          </div>
          <h2 className="text-3xl font-black mb-4" style={{ color: 'var(--text-main)' }}>Oops!</h2>
          <p className="mb-8 font-medium" style={{ color: 'var(--text-muted)' }}>{error}</p>
          <button
            onClick={() => navigate('/tech-library')}
            className="px-8 py-3 bg-green-600 text-white rounded-2xl font-bold hover:bg-green-700 transition-colors shadow-lg shadow-green-500/20"
          >
            Return to Library
          </button>
        </div>
      </div>
    );
  }

  const currentTopic = tutorial.topics?.[currentTopicIndex] || { title: tutorial.title, content: tutorial.description };
  const isRoadmap = tutorial.type === 'roadmap' || tutorial.category === 'Roadmaps';

  return (
    <div className="min-h-screen font-sans selection:bg-green-500/30" style={{ background: 'var(--bg-main)', color: 'var(--text-main)', transition: 'all 0.3s ease' }}>
      <SEO
        title={currentTopic.title}
        description={tutorial.description}
        keywords={`${currentTopic.title}, ${tutorial.category}, CodeCampus, SnapED Tutorial, Programming, ${tutorial.title}`}
        ogImage={tutorial.thumbnail}
      />
      {/* Progress Bar */}
      <div
        style={{ width: `${scrollProgress}%` }}
        className="fixed top-0 left-0 h-1 bg-green-600 z-[60] transition-all duration-300"
      />

      {/* Header / Navigation Overlay */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b" style={{ background: 'var(--glass)', borderColor: 'var(--glass-border)' }}>
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <button
            onClick={() => navigate('/tech-library')}
            className="flex items-center gap-3 transition-colors group"
            style={{ color: 'var(--text-muted)' }}
          >
            <div className="w-10 h-10 rounded-xl flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-all" style={{ background: 'var(--bg-surface)' }}>
              <FiArrowLeft size={20} />
            </div>
            <span className="font-bold text-sm uppercase tracking-widest block">Back to Library</span>
          </button>

          <div className="flex items-center gap-4">
            <button className="p-2.5 text-gray-400 hover:text-green-400 transition-colors"><FiBookmark size={20} /></button>
            <button className="p-2.5 text-gray-400 hover:text-green-400 transition-colors"><FiShare2 size={20} /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 pt-32 pb-20 flex flex-col lg:flex-row gap-12">
        {/* Sidebar - Desktop */}
        <aside className="lg:w-80 flex-shrink-0 lg:sticky lg:top-32 h-fit">
          <div className="p-6 rounded-[2rem] border backdrop-blur-md" style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}>
            <div className="flex items-center gap-3 mb-8 px-2">
              <FiLayers className="text-green-500" size={24} />
              <h3 className="font-black text-lg tracking-tight">Curriculum</h3>
            </div>

            <div className="space-y-2">
              {tutorial.topics && tutorial.topics.length > 0 ? (
                tutorial.topics.map((topic, index) => (
                  <button
                    key={index}
                    onClick={() => handleTopicChange(index)}
                    className={`w-full text-left px-5 py-4 rounded-2xl transition-all duration-300 flex items-center gap-4 ${currentTopicIndex === index
                      ? 'bg-green-600 text-white shadow-lg shadow-green-500/20'
                      : 'hover:bg-green-500/5'
                      }`}
                    style={{ color: currentTopicIndex === index ? '#fff' : 'var(--text-muted)' }}
                  >
                    <div className={`w-2 h-2 rounded-full ${currentTopicIndex === index ? 'bg-white' : 'bg-gray-700'}`} />
                    <span className="text-sm font-bold truncate">{topic.title}</span>
                  </button>
                ))
              ) : (
                <button className="w-full text-left px-5 py-4 rounded-2xl bg-green-600 text-white flex items-center gap-4">
                  <div className="w-2 h-2 rounded-full bg-white" />
                  <span className="text-sm font-bold">Introduction</span>
                </button>
              )}
            </div>
          </div>
        </aside>

        {/* Content Area */}
        <main className="flex-1">
          <motion.article
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            key={currentTopicIndex}
            className="border rounded-[2.5rem] p-8 md:p-12 backdrop-blur-md relative overflow-hidden"
            style={{ background: 'var(--bg-card)', borderColor: 'var(--border)' }}
          >
            {/* Decorative Background Glow */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-green-600/10 blur-[100px] pointer-events-none" />

            <header className="mb-12 relative z-10">
              <div className="flex items-center gap-4 mb-6">
                <span className="px-4 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-400 text-[10px] font-black uppercase tracking-[0.2em]">
                  {tutorial.category || 'Tutorial'}
                </span>
                <div className="flex items-center gap-2 text-gray-500 text-xs font-bold uppercase tracking-wider">
                  <FiClock /> 15 MIN READ
                </div>
              </div>

              <motion.button
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => navigate('/tech-library')}
                className="flex items-center gap-2 mb-6 text-green-500 hover:text-green-600 font-bold text-sm transition-all group"
              >
                <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                <span>Back to Library</span>
              </motion.button>

              <h1 className="text-4xl md:text-5xl font-black mb-6 leading-[1.1] tracking-tighter" style={{ color: 'var(--text-main)' }}>
                {currentTopic.title}
              </h1>
              <p className="text-xl font-medium leading-relaxed max-w-3xl" style={{ color: 'var(--text-muted)' }}>
                {tutorial.description}
              </p>
            </header>

            <div className="relative z-10 prose prose-invert prose-green max-w-none">
              {isRoadmap && (
                <div className="mb-12">
                  <RoadmapView data={tutorial.roadmapData || tutorial.topics?.[0]} />
                </div>
              )}
              {/* The actual sanitized HTML content */}
              <div
                className="tutorial-content-renderer leading-relaxed text-lg"
                style={{ color: 'var(--text-main)', opacity: 0.9 }}
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(currentTopic.content) }}
              />

              <div className="my-12">
                <BannerAd />
              </div>
            </div>

            {/* Navigation Footer */}
            <div className="mt-16 pt-10 border-t flex items-center justify-between gap-6" style={{ borderColor: 'var(--border)' }}>
              {currentTopicIndex > 0 ? (
                <button
                  onClick={() => handleTopicChange(currentTopicIndex - 1)}
                  className="px-6 py-4 rounded-2xl bg-green-500/5 hover:bg-green-500/10 font-bold transition-all flex items-center gap-3"
                  style={{ color: 'var(--text-main)' }}
                >
                  <FiArrowLeft /> Previous
                </button>
              ) : <div></div>}

              {tutorial.topics && currentTopicIndex < tutorial.topics.length - 1 && (
                <button
                  onClick={() => handleTopicChange(currentTopicIndex + 1)}
                  className="px-8 py-4 rounded-2xl bg-green-600 hover:bg-green-700 text-white font-bold transition-all shadow-xl shadow-green-500/20"
                >
                  Next Topic
                </button>
              )}
            </div>

            {/* Back to Library Footer Button */}
            <div className="mt-12 flex justify-center">
              <button
                onClick={() => navigate('/tech-library')}
                className="flex items-center gap-3 px-8 py-4 rounded-2xl bg-neutral-900 border border-white/10 text-gray-400 hover:text-white hover:bg-neutral-800 transition-all font-bold group"
              >
                <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" />
                Return to Library
              </button>
            </div>
          </motion.article>
        </main>
      </div>

      <Footer />

      <style>{`
                .tutorial-content-renderer h1, .tutorial-content-renderer h2, .tutorial-content-renderer h3 {
                    color: var(--text-main);
                    font-weight: 900;
                    margin-top: 2rem;
                    margin-bottom: 1.5rem;
                    letter-spacing: -0.02em;
                }
                .tutorial-content-renderer h2 { font-size: 2rem; border-left: 4px solid var(--accent); padding-left: 1rem; }
                .tutorial-content-renderer p { margin-bottom: 1.5rem; }
                .tutorial-content-renderer ul, .tutorial-content-renderer ol {
                    margin-bottom: 1.5rem;
                    padding-left: 1.5rem;
                    color: var(--text-muted);
                }
                .tutorial-content-renderer li { margin-bottom: 0.5rem; }
                .tutorial-content-renderer pre {
                    background: var(--bg-card);
                    border: 1px solid var(--border);
                    padding: 1.5rem;
                    border-radius: 1.5rem;
                    margin: 2rem 0;
                    overflow-x: auto;
                    font-family: 'JetBrains Mono', monospace;
                    font-size: 0.9rem;
                    color: var(--text-main);
                }
                .tutorial-content-renderer code {
                    background: var(--bg-surface);
                    padding: 0.2rem 0.4rem;
                    border-radius: 0.4rem;
                    font-size: 0.9em;
                }
                .tutorial-content-renderer blockquote {
                    border-left: 4px solid var(--accent);
                    background: rgba(34,197,94,0.05);
                    padding: 1.5rem;
                    border-radius: 0 1rem 1rem 0;
                    margin: 2rem 0;
                    font-style: italic;
                    color: var(--text-muted);
                }
            `}</style>
    </div>
  );
};

const TutorialSkeleton = ({ isDarkMode }) => (
  <div className="min-h-screen pt-32 px-6" style={{ background: 'var(--bg-main)' }}>
    <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-12 animate-pulse">
      <div className="lg:w-80 h-96 rounded-[2rem]" style={{ background: 'var(--bg-card)' }} />
      <div className="flex-1">
        <div className="h-12 w-48 rounded-full mb-8" style={{ background: 'var(--bg-card)' }} />
        <div className="h-20 w-3/4 rounded-3xl mb-12" style={{ background: 'var(--bg-card)' }} />
        <div className="space-y-6">
          <div className="h-4 w-full rounded-full" style={{ background: 'var(--bg-card)' }} />
          <div className="h-4 w-full rounded-full" style={{ background: 'var(--bg-card)' }} />
          <div className="h-4 w-2/3 rounded-full" style={{ background: 'var(--bg-card)' }} />
          <div className="h-64 w-full rounded-[2rem] mt-12" style={{ background: 'var(--bg-card)' }} />
        </div>
      </div>
    </div>
  </div>
);

export default TutorialDetail;